import { paths } from 'src/router/paths'
import { alignAnimationsToPageClock } from 'src/utils/animationClock'

export const APP_SHELL_CONTENT_ID = 'app-shell-content'

const BOOT_SCREEN_ID = 'app-boot'
const FOCUSED_PATHS = [
  '/authentication',
  paths.termsAndConditions,
  paths.workspaces.new,
  paths.workspaces.select
]
const SIDEBAR_SELECTOR = '[data-shell-chrome="sidebar"]'
const PROFILE_SELECTOR = '[data-shell-chrome="profile"]'

type ShellChrome = { sidebar: Node; profile: Node | null }

let holders = 0
let isInitialHoldReleased = false
let pendingHide = 0
let rememberedChrome: ShellChrome | null = null
let regionObserver: ResizeObserver | null = null

const findBootScreen = () => document.getElementById(BOOT_SCREEN_ID)

export const hasBootScreen = () => findBootScreen() !== null

export const isBootScreenShown = () => {
  const bootScreen = findBootScreen()

  return bootScreen !== null && !bootScreen.hidden
}

export const bootScopeFor = (pathname: string) =>
  FOCUSED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  )
    ? 'focused'
    : 'shell'

const COMPONENT_CSS_VAR_SCOPE = '[class*="-css-var"]'

const freezeComponentCssVariables = (source: Element, copy: Element) => {
  const sources = [source, ...source.querySelectorAll(COMPONENT_CSS_VAR_SCOPE)]
  const copies = [copy, ...copy.querySelectorAll(COMPONENT_CSS_VAR_SCOPE)]

  sources.forEach((node, index) => {
    const computed = getComputedStyle(node)
    const target = copies[index] as HTMLElement

    for (let i = 0; i < computed.length; i += 1) {
      const property = computed[i]

      if (property.startsWith('--ant-')) {
        target.style.setProperty(property, computed.getPropertyValue(property))
      }
    }
  })
}

const inertCopyOf = (element: Element) => {
  const copy = element.cloneNode(true) as Element

  freezeComponentCssVariables(element, copy)
  copy.removeAttribute('data-shell-chrome')
  copy.removeAttribute('id')
  copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'))

  return copy
}

const cloneLiveChrome = (root: ParentNode): ShellChrome | null => {
  const sidebar = root.querySelector(SIDEBAR_SELECTOR)
  const profile = root.querySelector(PROFILE_SELECTOR)

  return sidebar
    ? {
        sidebar: inertCopyOf(sidebar),
        profile: profile && inertCopyOf(profile)
      }
    : null
}

export const rememberShellChrome = (root: HTMLElement | null) => {
  rememberedChrome = (root && cloneLiveChrome(root)) ?? rememberedChrome
}

const visibleContentRegion = () => {
  const region = document.getElementById(APP_SHELL_CONTENT_ID)
  const rect = region?.getBoundingClientRect()

  return region && rect && rect.width > 0 && rect.height > 0 ? region : null
}

const resetPlacement = (bootScreen: HTMLElement) => {
  regionObserver?.disconnect()
  regionObserver = null
  delete bootScreen.dataset.region
  delete bootScreen.dataset.chrome
  bootScreen.style.removeProperty('top')
  bootScreen.style.removeProperty('left')
  bootScreen.style.removeProperty('width')
  bootScreen.style.removeProperty('height')
}

const alignCopiedChrome = (bootScreen: HTMLElement) =>
  bootScreen
    .querySelectorAll('[data-chrome-slot]')
    .forEach(alignAnimationsToPageClock)

const showShellChrome = (bootScreen: HTMLElement, chrome: ShellChrome) => {
  const sidebarSlot = bootScreen.querySelector('[data-chrome-slot="sidebar"]')
  const profileSlot = bootScreen.querySelector('[data-chrome-slot="profile"]')

  sidebarSlot?.replaceChildren(chrome.sidebar.cloneNode(true))
  if (chrome.profile) {
    profileSlot?.replaceChildren(chrome.profile.cloneNode(true))
  }
  bootScreen.dataset.chrome = 'real'
  alignCopiedChrome(bootScreen)
}

const coverWholeScreen = (bootScreen: HTMLElement) => {
  resetPlacement(bootScreen)

  const chrome = cloneLiveChrome(document) ?? rememberedChrome

  if (chrome) {
    showShellChrome(bootScreen, chrome)
  }
}

const coverContentRegion = (bootScreen: HTMLElement, region: HTMLElement) => {
  const place = () => {
    const { top, left, width, height } = region.getBoundingClientRect()

    if (width === 0 || height === 0) {
      coverWholeScreen(bootScreen)

      return
    }

    bootScreen.style.top = `${top}px`
    bootScreen.style.left = `${left}px`
    bootScreen.style.width = `${width}px`
    bootScreen.style.height = `${height}px`
  }

  bootScreen.dataset.region = ''
  place()
  regionObserver = new ResizeObserver(place)
  regionObserver.observe(region)
}

const placeBootScreen = (bootScreen: HTMLElement) => {
  const region = visibleContentRegion()

  if (region) {
    resetPlacement(bootScreen)
    coverContentRegion(bootScreen, region)

    return
  }

  coverWholeScreen(bootScreen)
}

const hideWhenNobodyHolds = () => {
  cancelAnimationFrame(pendingHide)
  pendingHide = requestAnimationFrame(() => {
    if (holders === 0 && isInitialHoldReleased) {
      findBootScreen()?.setAttribute('hidden', '')
      regionObserver?.disconnect()
      regionObserver = null
    }
  })
}

export const holdBootScreen = (label: string, pathname: string) => {
  holders += 1
  cancelAnimationFrame(pendingHide)

  const bootScreen = findBootScreen()

  if (bootScreen?.hidden) {
    document.documentElement.dataset.bootScope = bootScopeFor(pathname)
    bootScreen.setAttribute('aria-label', label)
    placeBootScreen(bootScreen)
    bootScreen.removeAttribute('hidden')
    alignCopiedChrome(bootScreen)
  }

  return () => {
    holders -= 1
    hideWhenNobodyHolds()
  }
}

export const releaseInitialBootScreen = () => {
  isInitialHoldReleased = true
  hideWhenNobodyHolds()
}
